import { averageSpeed, buildLegs, type PerformanceReport } from './calc.js';
import { reportConsumption } from './fuel.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Sefer özeti (ADR 0082 Ek §2; ortağın `sefer-3.jpeg` "Voyage Summary", S9): seyir, liman, demir,
 * sürüklenme ve bekleme süreleri, mesafe, yakıt türüne ve döneme göre tüketim, dönemler ve
 * bacaklar. Raporlardan türetilir, saklanmaz (S12): rapor düzeltilince özet de değişir. Yalnız
 * gönderilmiş raporlar okunur (performans gibi; ADR 0022 §4). Saf işlev; saat UTC.
 */

export const VOYAGE_STATES = ['sea', 'port', 'anchorage', 'drift', 'waiting'] as const;
export type VoyageState = (typeof VOYAGE_STATES)[number];

/** Gemi bu durumlarda bir yerde kalır; aralarındaki seyirler bacaktır (sefer-6 Timeline). */
const STAYS: ReadonlySet<VoyageState> = new Set(['port', 'anchorage', 'drift']);

export interface SummaryReport extends PerformanceReport {
  /** Verilmezse gönderilmiş sayılır. */
  status?: string;
}

export interface SummaryPeriod {
  state: VoyageState;
  start: string;
  end: string;
  hours: number;
  distanceNm: number;
  /** Limanda liman adı; diğerlerinde `null`. */
  place: string | null;
}

export interface SummaryLeg {
  no: number;
  start: string;
  end: string;
  hours: number;
  distanceNm: number;
  avgSpeedKn: number | null;
  /** Kalkış limanı ya da önceki liman kalışı; bilinmiyorsa `null`. */
  from: string | null;
  /** Bacaktan sonraki liman kalışı; yoksa `null`. */
  to: string | null;
}

export interface FuelConsumption {
  fuel: string;
  total: number;
  byState: Record<VoyageState, number>;
}

export interface VoyageSummary {
  start: string | null;
  end: string | null;
  /** Durum başına süre (saat), toplam ve boğaz/kanal geçişi (seyrin içinde). */
  hours: Record<VoyageState | 'total' | 'passage', number>;
  distanceNm: number;
  /** Seyir hızı: seyirde (bekleme dahil) gidilen mesafe / seyir süresi. */
  avgSpeedKn: number | null;
  fuels: FuelConsumption[];
  /** Bütün yakıtların toplamı; tüketim yazılmamışsa `null`. */
  consumptionMt: number | null;
  periods: SummaryPeriod[];
  legs: SummaryLeg[];
  reportCount: number;
}

const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};
const hoursBetween = (from: string, to: string) => (Date.parse(to) - Date.parse(from)) / 3_600_000;
const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/** Raporun sonrasındaki durum; olay durumu değiştirmiyorsa öncekinin aynısı. */
const AFTER: Partial<Record<VoyageReportKind, VoyageState>> = {
  departure: 'sea',
  cosp: 'sea',
  eosp: 'sea',
  strait_entry: 'sea',
  strait_exit: 'sea',
  canal_entry: 'sea',
  canal_exit: 'sea',
  anchorage_end: 'sea',
  drift_end: 'sea',
  waiting_end: 'sea',
  arrival: 'port',
  port_daily: 'port',
  anchorage_start: 'anchorage',
  anchorage_daily: 'anchorage',
  drift_start: 'drift',
  drift_daily: 'drift',
  waiting_start: 'waiting',
};

function stateAfter(report: PerformanceReport, previous: VoyageState | null): VoyageState {
  const next = AFTER[report.kind];
  if (next) return next;
  if (previous) return previous;
  // Seferin ortasından başlayan ilk rapor (ADR 0047 §2): yük olayı limanda, diğerleri denizde
  return report.kind === 'cargo_start' || report.kind === 'cargo_complete' ? 'port' : 'sea';
}

function portOf(report: PerformanceReport): string | null {
  if (report.kind === 'arrival' || report.kind === 'departure') return text(report.data.port);
  if (report.kind === 'port_daily') return text(record(report.data.location).port);
  return null;
}

const zeroStates = (): Record<VoyageState, number> => ({
  sea: 0,
  port: 0,
  anchorage: 0,
  drift: 0,
  waiting: 0,
});

/**
 * Seferin özeti. Her rapor aralığı (`reports[i - 1]` → `reports[i]`) önceki raporun bıraktığı
 * durumdadır: kalkış, COSP, EOSP, demir alma, sürüklenme bitişi seyir; Berthing liman; demir,
 * sürüklenme ve bekleme kendi durumu. Mesafe ve tüketim aralığı kapatan rapordan gelir; ilk
 * raporun "son rapordan beri" değerleri önceki döneme aittir, sayılmaz. Sefer bittiyse
 * (`options.end`, `voyagePeriods`) son rapordan bitişe kadar son durum sürer (ör. limanda kalış).
 */
export function voyageSummary(
  reports: readonly SummaryReport[],
  options: { end: string | null },
): VoyageSummary {
  return summarizeVoyage(reports, options).summary;
}

/**
 * Özetin aralıkları (ADR 0090 §2): `reports[i - 1]` → `reports[i]`, aralığın durumu, dönemi ve
 * bacağı. Yakıt analizi tüketimi bu aralıklarla dönemlere ve bacaklara dağıtır; özetle aynı
 * yürüyüş, ikinci bir durum makinesi yazılmaz.
 */
export interface SummaryInterval<R extends SummaryReport = SummaryReport> {
  previous: R;
  report: R;
  state: VoyageState;
  hours: number;
  distanceNm: number;
  /** Aralıkta boğaz ya da kanal geçişi açık (seyrin içinde). */
  passage: boolean;
  /** `summary.periods` içindeki dönem. */
  period: number;
  /** `summary.legs` içindeki bacak; kalışta (liman, demir, sürüklenme) `null`. */
  leg: number | null;
}

/** Özet ve aralıkları; yalnız gönderilmiş raporlar, zaman sırasıyla. */
export function summarizeVoyage<R extends SummaryReport>(
  reports: readonly R[],
  options: { end: string | null },
): { summary: VoyageSummary; intervals: SummaryInterval<R>[] } {
  const sorted = reports
    .filter((r) => (r.status ?? 'submitted') === 'submitted')
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  const hours = { ...zeroStates(), total: 0, passage: 0 };
  const first = sorted[0];
  const last = sorted.at(-1);
  if (!first || !last) {
    const summary: VoyageSummary = {
      start: null,
      end: null,
      hours,
      distanceNm: 0,
      avgSpeedKn: null,
      fuels: [],
      consumptionMt: null,
      periods: [],
      legs: [],
      reportCount: 0,
    };
    return { summary, intervals: [] };
  }

  const legs = buildLegs(sorted);
  const periods: (SummaryPeriod & { from: string | null })[] = [];
  const fuels = new Map<string, FuelConsumption>();
  let distanceNm = 0;
  let seaDistance = 0;
  let state = stateAfter(first, null);
  let place = state === 'port' ? portOf(first) : null;
  let from: string | null = first.kind === 'departure' ? portOf(first) : null;

  const intervals: Omit<SummaryInterval<R>, 'leg'>[] = [];
  const add = (start: string, end: string, distance: number): number => {
    const span = hoursBetween(start, end);
    hours[state] += span;
    distanceNm += distance;
    if (state === 'sea' || state === 'waiting') seaDistance += distance;
    const open = periods.at(-1);
    if (open && open.state === state && open.place === place && open.end === start) {
      open.end = end;
      open.hours += span;
      open.distanceNm += distance;
    } else {
      periods.push({ state, start, end, hours: span, distanceNm: distance, place, from });
    }
    return periods.length - 1;
  };

  for (let i = 1; i < sorted.length; i++) {
    const report = sorted[i] as R;
    const previous = sorted[i - 1] as R;
    const leg = legs[i] as (typeof legs)[number];
    const period = add(previous.reportedAt, report.reportedAt, leg.distanceNm);
    const passage = leg.open.has('strait') || leg.open.has('canal');
    if (passage) hours.passage += leg.hours;
    intervals.push({
      previous,
      report,
      state,
      hours: leg.hours,
      distanceNm: leg.distanceNm,
      passage,
      period,
    });
    for (const [fuel, value] of reportConsumption(report)) {
      const row = fuels.get(fuel) ?? { fuel, total: 0, byState: zeroStates() };
      row.total += value;
      row.byState[state] += value;
      fuels.set(fuel, row);
    }
    state = stateAfter(report, state);
    place = state === 'port' ? (portOf(report) ?? place) : null;
    from = report.kind === 'departure' ? portOf(report) : place;
  }
  const end =
    options.end && Date.parse(options.end) > Date.parse(last.reportedAt) ? options.end : null;
  if (end) add(last.reportedAt, end, 0);

  for (const key of [...Object.keys(zeroStates()), 'passage'] as (keyof typeof hours)[]) {
    hours[key] = round(hours[key]);
  }
  hours.total = round(VOYAGE_STATES.reduce((sum, s) => sum + hours[s], 0));
  const fuelRows = [...fuels.values()].map((row) => ({
    fuel: row.fuel,
    total: round(row.total),
    byState: Object.fromEntries(VOYAGE_STATES.map((s) => [s, round(row.byState[s])])) as Record<
      VoyageState,
      number
    >,
  }));

  const { legs: summaryLegRows, periodLeg } = summaryLegs(periods);
  const summary: VoyageSummary = {
    start: first.reportedAt,
    end: end ?? last.reportedAt,
    hours,
    distanceNm: round(distanceNm),
    avgSpeedKn: averageSpeed(seaDistance, hours.sea + hours.waiting),
    fuels: fuelRows,
    consumptionMt:
      fuelRows.length > 0 ? round(fuelRows.reduce((sum, row) => sum + row.total, 0)) : null,
    periods: periods.map(({ from: _from, ...p }) => ({
      ...p,
      hours: round(p.hours),
      distanceNm: round(p.distanceNm),
    })),
    legs: summaryLegRows,
    reportCount: sorted.length,
  };
  return {
    summary,
    intervals: intervals.map((interval) => ({
      ...interval,
      leg: periodLeg[interval.period] ?? null,
    })),
  };
}

/** Kalışlar arasındaki seyir ve bekleme dönemleri birer bacaktır; `periodLeg` dönemin bacağı. */
function summaryLegs(periods: readonly (SummaryPeriod & { from: string | null })[]): {
  legs: SummaryLeg[];
  periodLeg: (number | null)[];
} {
  const legs: SummaryLeg[] = [];
  const periodLeg: (number | null)[] = [];
  let open: SummaryLeg | null = null;
  periods.forEach((period, index) => {
    if (STAYS.has(period.state)) {
      if (open) open.to = period.state === 'port' ? period.place : null;
      open = null;
      periodLeg.push(null);
      return;
    }
    if (!open) {
      const before = periods[index - 1];
      open = {
        no: legs.length + 1,
        start: period.start,
        end: period.end,
        hours: 0,
        distanceNm: 0,
        avgSpeedKn: null,
        from: before ? (before.state === 'port' ? before.place : null) : period.from,
        to: null,
      };
      legs.push(open);
    }
    periodLeg.push(legs.length - 1);
    open.end = period.end;
    open.hours += period.hours;
    open.distanceNm += period.distanceNm;
  });
  return {
    legs: legs.map((leg) => ({
      ...leg,
      hours: round(leg.hours),
      distanceNm: round(leg.distanceNm),
      avgSpeedKn: averageSpeed(leg.distanceNm, leg.hours),
    })),
    periodLeg,
  };
}

/** Geminin bugünkü durumu: son gönderilmiş raporun bıraktığı durum; rapor yoksa `null`. */
export function vesselState(reports: readonly SummaryReport[]): VoyageState | null {
  const sorted = reports
    .filter((r) => (r.status ?? 'submitted') === 'submitted')
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  let state: VoyageState | null = null;
  for (const report of sorted) state = stateAfter(report, state);
  return state;
}

/**
 * Ticari durum (sefer-6 "Commercial Status"): son gönderilmiş kira olayı. Kira dönemi kaydı (CP;
 * README S15) gelene dek yalnız On Hire / Off Hire raporları okunur; yoksa "Not Assigned".
 */
export function commercialStatus(
  reports: readonly SummaryReport[],
): 'on_hire' | 'off_hire' | 'not_assigned' {
  const last = reports
    .filter(
      (r) =>
        (r.status ?? 'submitted') === 'submitted' &&
        (r.kind === 'on_hire' || r.kind === 'off_hire'),
    )
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
    .at(-1);
  return last ? (last.kind as 'on_hire' | 'off_hire') : 'not_assigned';
}
